"use client"
import { useEffect, useState, useMemo } from "react"
import { useForm } from "react-hook-form"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Button,
  Textarea,
  Box,
  Flex,
  Select,
  ModalBody,
  ModalFooter,
  useToast,
  FormControl,
  FormErrorMessage,
  useDisclosure,
  Tag,
  Avatar,
  Badge,
  Text,
  VStack,
  Divider,
} from "@chakra-ui/react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { fetchToken } from "../../../common/FetchCookie"
import axios from "axios"
import { TimeDifference } from "../../../common/TimeDifference"
import {
  fetchPublicJobDetailAsync,
  selectPublicJobDetail,
} from "../../../jobList/publicJobDescription/publicJobDescriptionSlice"
import { updateBookmarkAsync } from "../SavedJobs/SavedJobsSlice"
import { BASE_URL, CANDIDATE, CANDIDATE_JOB_APPLY, EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../../common/URLs"
import {
  FaFileInvoice,
  FaRegBookmark,
  FaBookmark,
  FaMapMarkerAlt,
  FaRegClock,
  FaBriefcase,
  FaBuilding,
  FaUsers,
  FaCalendarAlt,
  FaVideo,
  FaGraduationCap,
  FaUserTie,
  FaShare,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
} from "react-icons/fa"
import { fetchUserRole } from "../../../common/FetchUserRole"
import { Footer } from "../../../footer/footer"
import RelatedJobs from "../RelatedJobs/RelatedJobs"

/**
 * Flatten job.job_educations from the public job API into display-ready chains:
 * returns array of { uuid: lastNodeUuid, title: "Level / Course / Speciality" }
 */
const deriveEducationChains = (jobEducations = []) => {
  const out = []
  for (const item of jobEducations) {
    if (item?.core_education_course_specialty) {
      const spec = item.core_education_course_specialty
      const course = spec?.core_job_education_course
      const level = course?.core_job_education
      const label = [level?.title, course?.title, spec?.title].filter(Boolean).join(" / ")
      if (spec?.uuid) out.push({ uuid: spec.uuid, title: label })
      continue
    }
    if (item?.core_education_course) {
      const course = item.core_education_course
      const level = course?.core_job_education
      const label = [level?.title, course?.title].filter(Boolean).join(" / ")
      if (course?.uuid) out.push({ uuid: course.uuid, title: label })
      continue
    }
    if (item?.core_education) {
      const level = item.core_education
      if (level?.uuid) out.push({ uuid: level.uuid, title: level.title })
    }
  }
  // dedupe by uuid
  const seen = new Set()
  return out.filter((x) => {
    if (seen.has(x.uuid)) return false
    seen.add(x.uuid)
    return true
  })
}

const JobSingle2 = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [hasResume, setHasResume] = useState(false)
  const { uuid } = useParams()
  const location = useLocation()
  const job = useSelector(selectPublicJobDetail)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const toast = useToast()

  // State for resume preview when uploading
  const [resumePreview, setResumePreview] = useState(null)
  // Initialize bookmark state directly from job data
  const [bookmarked, setBookmarked] = useState(false)
  const [showShareOptions, setShowShareOptions] = useState(false)
  const [shareUrl, setShareUrl] = useState("")

  useEffect(() => {
    if (uuid) {
      dispatch(fetchPublicJobDetailAsync({ uuid }))
    }
    window.scrollTo(0, 0)
    const currentUrl = window.location.origin + location.pathname + location.search
    setShareUrl(currentUrl)
  }, [uuid, dispatch, location])

  // Close share dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareOptions && !event.target.closest(".share-dropdown")) {
        setShowShareOptions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showShareOptions])

  // Update bookmark state when job data changes
  useEffect(() => {
    if (job && job.candidate_job_saved_count !== undefined) {
      setBookmarked(job.candidate_job_saved_count === 1)
    }
  }, [job])

  const handleBookmarkClick = async () => {
    try {
      const role = await new Promise((resolve) => {
        fetchUserRole((r) => resolve(r))
      })

      if (role !== "candidate") {
        localStorage.setItem("redirectAfterLogin", window.location.pathname)
        toast({
          title: "Please login first",
          description: "You need to login as a candidate to save or apply for a job.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
        navigate("/login")
        return
      }

      const newStatus = bookmarked ? "remove" : "save"
      setBookmarked(!bookmarked)

      await dispatch(updateBookmarkAsync({ job_uuid: job.uuid, status: newStatus }))

      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"} successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to update bookmark:", error)
      setBookmarked(bookmarked)
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  const handleApplyClick = () => {
    fetchUserRole((role) => {
      if (role === "candidate") {
        onOpen()
      } else {
        localStorage.setItem("redirectAfterLogin", window.location.pathname)
        toast({
          title: "Please login first",
          description: "You need to login as a candidate to save or apply for a job.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
        navigate("/login")
      }
    })
  }

  const handleShareClick = () => {
    setShowShareOptions(!showShareOptions)
  }

  // Custom share functions using native URLs
  const shareOnFacebook = ({ url, quote, hashtag }) => {
    const shareText = quote || ""
    const hashtagText = hashtag ? `%23${hashtag.replace("#", "")}` : ""
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(
      shareText + " " + hashtagText
    )}`
    window.open(facebookUrl, "_blank", "width=600,height=400,scrollbars=yes,resizable=yes")
    setShowShareOptions(false)
  }

  const shareOnTwitter = ({ url, title, hashtags = [] }) => {
    const hashtagText = hashtags.map((tag) => `#${tag}`).join(" ")
    const tweetText = `${title} ${hashtagText}`
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
      tweetText
    )}`
    window.open(twitterUrl, "_blank", "width=600,height=400,scrollbars=yes,resizable=yes")
    setShowShareOptions(false)
  }

  const shareOnLinkedIn = ({ url, title, summary }) => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      url
    )}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`
    window.open(linkedinUrl, "_blank", "width=600,height=400,scrollbars=yes,resizable=yes")
    setShowShareOptions(false)
  }

  // Custom share button component
  const ShareButton = ({ platform, onClick, children, className = "" }) => {
    const baseClasses =
      "flex items-center justify-center w-10 h-10 text-white rounded-full hover:opacity-80 transition-all duration-200 transform hover:scale-105"
    const platformClasses = {
      facebook: "bg-blue-600 hover:bg-blue-700",
      twitter: "bg-blue-400 hover:bg-blue-500",
      linkedin: "bg-blue-700 hover:bg-blue-800",
    }

    return (
      <button
        onClick={onClick}
        className={`${baseClasses} ${platformClasses[platform]} ${className}`}
        aria-label={`Share on ${platform}`}
      >
        {children}
      </button>
    )
  }

  // Custom icon component to match react-share icon style
  const CustomIcon = ({ platform, size = 20 }) => {
    const iconProps = {
      size,
      color: "white",
    }

    switch (platform) {
      case "facebook":
        return <FaFacebook {...iconProps} />
      case "twitter":
        return <FaTwitter {...iconProps} />
      case "linkedin":
        return <FaLinkedin {...iconProps} />
      default:
        return null
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast({
        title: "Link copied!",
        description: "Job link has been copied to clipboard.",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      })
      setShowShareOptions(false)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand("copy")
        toast({
          title: "Link copied!",
          description: "Job link has been copied to clipboard.",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        })
      } catch (err) {
        toast({
          title: "Copy failed",
          description: "Please copy the link manually.",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        })
      }
      document.body.removeChild(textArea)
      setShowShareOptions(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/rtf",
        "text/plain",
      ]
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: "Please upload PDF, DOCX, RTF, or TXT files.",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
        return
      }

      const maxSizeInBytes = 5 * 1024 * 1024
      if (file.size > maxSizeInBytes) {
        toast({
          title: "File Too Large",
          description: "File size should not exceed 5MB.",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
        return
      }

      setValue("new_resume", file)

      const reader = new FileReader()
      reader.onloadend = () => {
        setResumePreview({
          name: file.name,
          type: file.type,
          size: file.size,
          preview: reader.result,
        })
      }
      reader.readAsDataURL(file)

      toast({
        title: "Resume Uploaded",
        description: `${file.name} successfully uploaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const removeResume = () => {
    setResumePreview(null)
    if (document.getElementById("fileUpload")) {
      document.getElementById("fileUpload").value = ""
    }
    setValue("new_resume", null)
  }

  const onSubmit = async (data) => {
    try {
      const token = await fetchToken()
      const hasResumeValue = data.hasResume === "true"

      const formData = new FormData()
      formData.append("job_uuid", job.uuid)
      formData.append("is_new_resume", hasResumeValue ? 1 : 0)
      formData.append("apply_note", data.apply_note || "")

      if (hasResumeValue && data.new_resume) {
        formData.append("new_resume", data.new_resume)
      }

      try {
        const response = await axios.post(`${BASE_URL}/${CANDIDATE}/${CANDIDATE_JOB_APPLY}`, formData, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "multipart/form-data",
          },
        })

        toast({
          title: "Application Submitted",
          description: "Your job application was submitted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        })

        onClose()
        dispatch(fetchPublicJobDetailAsync({ uuid }))

        return response.data
      } catch (err) {
        if (err.response) {
          if (err.response.status === 400) {
            toast({
              title: "Subscription Required",
              description: "You don't have a valid subscription to apply for this job.",
              status: "warning",
              duration: 5000,
              isClosable: true,
              position: "top-right",
            })
          } else {
            toast({
              title: "Application Failed",
              description: err.response.data.message || "Failed to submit job application.",
              status: "error",
              duration: 5000,
              isClosable: true,
              position: "top-right",
            })
          }
        } else if (err.request) {
          toast({
            title: "Network Error",
            description: "No response received from the server. Please check your connection.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          })
        } else {
          toast({
            title: "Error",
            description: "An error occurred while submitting the application.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          })
        }
        return null
      }
    } catch (error) {
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      return null
    }
  }

  // Function to check if content contains HTML tags
  const containsHTML = (str) => {
    if (!str) return false
    return /<[a-z][\s\S]*>/i.test(str)
  }

  // Function to convert plain text to HTML with proper formatting
  const formatPlainTextToHTML = (text) => {
    if (!text) return ""
    const lines = text.split("\n")
    let formattedHTML = ""
    let inList = false

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (line === "") {
        if (inList) {
          formattedHTML += "</ul>"
          inList = false
        }
        formattedHTML += "<br>"
        continue
      }
      if (line.match(/^[-•*]\s+/)) {
        if (!inList) {
          formattedHTML += "<ul>"
          inList = true
        }
        const listItem = line.replace(/^[-•*]\s+/, "")
        formattedHTML += `<li>${listItem}</li>`
      } else if (line.endsWith(":") && line.length < 50 && !line.includes(" - ")) {
        if (inList) {
          formattedHTML += "</ul>"
          inList = false
        }
        formattedHTML += `<h3><strong>${line}</strong></h3>`
      } else {
        if (inList) {
          formattedHTML += "</ul>"
          inList = false
        }
        formattedHTML += `<p>${line}</p>`
      }
    }
    if (inList) {
      formattedHTML += "</ul>"
    }
    return formattedHTML
  }

  // Enhanced CSS styles for job description rendering
  const jobDescriptionStyles = {
    lineHeight: "1.6",
    color: "inherit",
    fontSize: "16px",
  }

  const richTextContainerStyles = {
    "& p": {
      marginBottom: "1rem",
      lineHeight: "1.6",
    },
    "& ul, & ol": {
      paddingLeft: "1.5rem",
      marginBottom: "1rem",
      lineHeight: "1.6",
    },
    "& ul": {
      listStyleType: "disc",
    },
    "& ol": {
      listStyleType: "decimal",
    },
    "& li": {
      marginBottom: "0.5rem",
      lineHeight: "1.6",
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      fontWeight: "bold",
      marginTop: "1.5rem",
      marginBottom: "1rem",
      lineHeight: "1.4",
    },
    "& h1": {
      fontSize: "1.875rem",
      fontWeight: "800",
    },
    "& h2": {
      fontSize: "1.5rem",
      fontWeight: "700",
    },
    "& h3": {
      fontSize: "1.25rem",
      fontWeight: "600",
    },
    "& blockquote": {
      borderLeft: "4px solid #e2e8f0",
      paddingLeft: "1rem",
      fontStyle: "italic",
      margin: "1rem 0",
      backgroundColor: "#f7fafc",
      padding: "1rem",
      borderRadius: "0.375rem",
    },
    "& a": {
      color: "#3182ce",
      textDecoration: "underline",
    },
    "& a:hover": {
      color: "#2c5aa0",
    },
    "& strong, & b": {
      fontWeight: "bold",
    },
    "& em, & i": {
      fontStyle: "italic",
    },
    "& u": {
      textDecoration: "underline",
    },
    "& strike": {
      textDecoration: "line-through",
    },
    "& code": {
      backgroundColor: "#f1f5f9",
      padding: "0.125rem 0.25rem",
      borderRadius: "0.25rem",
      fontSize: "0.875rem",
      fontFamily: "monospace",
    },
    "& pre": {
      backgroundColor: "#f1f5f9",
      padding: "1rem",
      borderRadius: "0.375rem",
      overflow: "auto",
      marginBottom: "1rem",
    },
  }

  // Format salary for display
  const formatSalary = () => {
    if (!job) return "Salary not disclosed"
    if (job.salary_not_disclose === 1 || (!job.salary_min_amount && !job.salary_max_amount)) {
      return "Salary not disclosed"
    }

    const formatAmount = (amount) => {
      if (!amount) return "N/A"
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(amount)
    }

    const minSalary = formatAmount(job.salary_min_amount)
    const maxSalary = formatAmount(job.salary_max_amount)
    const period = job.salary_per_type ? ` ${job.salary_per_type}` : ""

    return `${minSalary} - ${maxSalary}${period}`
  }

  // Education chains derived from API
  const educationChains = useMemo(() => deriveEducationChains(job?.job_educations || []), [job])

  // Experience label (Fresher when 0 - 0)
  const experienceLabel = useMemo(() => {
    if (job?.min_job_exp === 0 && job?.max_job_exp === 0) return "Fresher"
    if (job?.min_job_exp != null && job?.max_job_exp != null) return `${job.min_job_exp} - ${job.max_job_exp} years`
    return "Not specified"
  }, [job])

  // Check for redirect after login
  useEffect(() => {
    const redirectPath = localStorage.getItem("redirectAfterLogin")
    if (redirectPath && uuid) {
      localStorage.removeItem("redirectAfterLogin")
      if (redirectPath !== window.location.pathname) {
        navigate(redirectPath)
      }
    }
  }, [navigate, uuid])

  const shareTitle = job ? `${job.job_title?.title || job.job_name} at ${job.hiring_company_name}` : "Job Opportunity"
  const shareDescription = job
    ? `Check out this job opportunity: ${job.job_title?.title || job.job_name} at ${job.hiring_company_name}`
    : "Check out this job opportunity"

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-2.5"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    )
  }

  return (
    <>
      {job ? (
        <div className="bg-white min-h-screen">
          {/* Job Header with Gradient */}
          <div className="bg-gradient-to-r  from-blue-700 via-sky-300 to-sky-100 rounded-b-3xl h-[90px] relative">
            <div className=" sm:px-24 px-6  relative h-full" >
              {/* Company Logo Container - Positioned for perfect alignment */}
              <div className="absolute -bottom-16 left-5 sm:left-24 flex items-center">
                <div className="w-32 h-32 bg-white overflow-hidden rounded-full shadow-md  flex items-center justify-center p-2 border border-gray-200">
                  {job.company_logo ? (
                    <img
                      src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}`}
                      alt={job.hiring_company_name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <FaBuilding className="w-16 h-16 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full sm:px-24 px-6 pt-24 pb-12" >
            <div className="flex flex-col md:flex-row justify-between">
              <div className="">
                {" "}
                {/* Add margin to align with logo */}
                <h1 className="text-3xl text-left font-bold text-gray-900">
                  {job.job_title?.title || job.job_name}{" "}
                  <span className="ml-2 text-sm">
                    {job.urgent_hiring_tag_enable === 1 && (
                      <Badge colorScheme="red" fontSize="0.8em" px={2} py={1} borderRadius="full">
                        Urgent Hiring
                      </Badge>
                    )}
                  </span>
                </h1>
                <div className="mt-2 text-xl text-blue-700 font-medium">{job.job_name}</div>
                <div className="mt-2 text-xl text-gray-700 font-medium">{job.hiring_company_name}</div>
                <div className="mt-2 text-gray-600 flex items-center flex-wrap gap-3">
                  <span className="flex items-center">
                    <FaMapMarkerAlt className="mr-1 text-blue-500" />
                    {job.job_locations?.map((loc, index) => (
                      <span key={index}>
                        {loc.job_location?.title}
                        {index < job.job_locations.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </span>
                  <span className="flex items-center">
                    <span className="mx-1">•</span>
                    <FaRegClock className="mr-1 text-blue-500" />
                    <TimeDifference dateTimeString={job.created_at} />
                  </span>
                  <span className="flex items-center">
                    <span className="mx-1">•</span>
                    <FaBriefcase className="mr-1 text-blue-500" />
                    {job.job_type || "Full Time"}
                  </span>
                </div>
                {/* Job Tags */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {job.work_location_type && (
                    <Badge colorScheme="blue" fontSize="0.8em" px={2} py={1} borderRadius="full">
                      {job.work_location_type}
                    </Badge>
                  )}
                  {job.job_shift && (
                    <Badge colorScheme="purple" fontSize="0.8em" px={2} py={1} borderRadius="full">
                      {job.job_shift}
                    </Badge>
                  )}
                  {job.job_tag && (
                    <Badge colorScheme="green" fontSize="0.8em" px={2} py={1} borderRadius="full">
                      {job.job_tag}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mt-6 md:mt-0 flex flex-wrap    gap-3 items-center ">
                {job.is_expired !== 1 && (
                  <button
                    onClick={handleBookmarkClick}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
                  >
                    {bookmarked ? (
                      <>
                        <FaBookmark className="mr-2 text-blue-500" />
                        Saved
                      </>
                    ) : (
                      <>
                        <FaRegBookmark className="mr-2" />
                        Save
                      </>
                    )}
                  </button>
                )}

                <div className="relative share-dropdown">
                  <button
                    onClick={handleShareClick}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
                  >
                    <FaShare className="mr-2" />
                    Share
                  </button>

                  {/* Share Options Dropdown */}
                  {showShareOptions && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                      <div className="py-2">
                        <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100">
                          Share this job
                        </div>
                        <div className="flex justify-around py-3">
                          <ShareButton
                            platform="facebook"
                            onClick={() =>
                              shareOnFacebook({
                                url: shareUrl,
                                quote: shareTitle,
                                hashtag: "#jobs",
                              })
                            }
                          >
                            <CustomIcon platform="facebook" size={20} />
                          </ShareButton>
                          <ShareButton
                            platform="twitter"
                            onClick={() =>
                              shareOnTwitter({
                                url: shareUrl,
                                title: shareTitle,
                                hashtags: ["jobs", "career", "hiring"],
                              })
                            }
                          >
                            <CustomIcon platform="twitter" size={20} />
                          </ShareButton>
                          <ShareButton
                            platform="linkedin"
                            onClick={() =>
                              shareOnLinkedIn({
                                url: shareUrl,
                                title: shareTitle,
                                summary: shareDescription,
                              })
                            }
                          >
                            <CustomIcon platform="linkedin" size={20} />
                          </ShareButton>
                        </div>
                        <button
                          onClick={copyToClipboard}
                          className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                        >
                          Copy link
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {job.is_expired === 1 ? (
                  <div className="px-4 py-2 rounded-md bg-red-50 text-red-600 text-sm font-medium shadow-sm">
                    Job expired
                  </div>
                ) : job.already_applied ? (
                  <div className="px-4 py-2 rounded-md bg-green-50 text-green-600 text-sm font-medium shadow-sm">
                    Applied
                  </div>
                ) : (
                  <button
                    onClick={handleApplyClick}
                    className="px-6 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 shadow-sm"
                  >
                    Apply Now
                  </button>
                )}
              </div>
            </div>

            {/* Job Content */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Left Column - Job Details */}
              <div className="md:col-span-2 space-y-8">
                {/* Job Summary */}
                <Box bg="blue.50" p={4} borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
                  <Flex wrap="wrap" gap={4}>
                    <Box flex="1" minW="150px">
                      <Text fontSize="sm" color="gray.500">
                        Salary
                      </Text>
                      <Text fontWeight="bold">{formatSalary()}</Text>
                    </Box>
                    <Box flex="1" minW="150px">
                      <Text fontSize="sm" color="gray.500">
                        Experience
                      </Text>
                      <Text fontWeight="bold">{experienceLabel}</Text>
                    </Box>
                    <Box flex="1" minW="150px">
                      <Text fontSize="sm" color="gray.500">
                        Job Type
                      </Text>
                      <Text fontWeight="bold">{job.job_type || "Not specified"}</Text>
                    </Box>
                    <Box flex="1" className="items-center" minW="150px">
                      <Text fontSize="sm" color="gray.500">
                        Vacancies
                      </Text>
                      <Text className="" fontWeight="bold">
                        {job.no_of_hire || "Not specified"}
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                {/* About the job */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">About the job</h2>
                  <div className="text-gray-700 bg-white rounded-lg">
                    <Box sx={richTextContainerStyles}>
                      {job.job_description && containsHTML(job.job_description) ? (
                        <div style={jobDescriptionStyles} dangerouslySetInnerHTML={{ __html: job.job_description }} />
                      ) : (
                        <div
                          style={jobDescriptionStyles}
                          dangerouslySetInnerHTML={{
                            __html: formatPlainTextToHTML(job.job_description || "No job description provided"),
                          }}
                        />
                      )}
                    </Box>
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Skills Required</h2>
                  <div className="bg-white  rounded-lg  ">
                    <div className="flex flex-wrap gap-2">
                      {Array.isArray(job.job_skills) && job.job_skills.length > 0 ? (
                        job.job_skills
                          .filter((skill) => skill.job_skill?.title)
                          .map((skill, index) => (
                            <Tag
                              key={skill.uuid || index}
                              size="md"
                              borderRadius="full"
                              variant="subtle"
                              colorScheme="blue"
                              py={1.5}
                              px={3}
                            >
                              {skill.job_skill.title}
                            </Tag>
                          ))
                      ) : (
                        <div className="text-gray-500">No specific skills mentioned</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Education (chains) */}
                {educationChains.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb={4}">Education Requirements</h2>
                    <div className="bg-white rounded-lg">
                      <div className="flex flex-wrap gap-2">
                        {educationChains.map((edu, index) => (
                          <Tag
                            key={edu.uuid || index}
                            size="md"
                            borderRadius="full"
                            variant="subtle"
                            colorScheme="purple"
                            py={1.5}
                            px={3}
                          >
                            {edu.title}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Benefits */}
                {job.additional_pay && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Additional Benefits</h2>
                    <div className="bg-white  rounded-lg  ">
                      <div className="flex flex-wrap gap-2">
                        {job.additional_pay.split(",").map((benefit, index) => (
                          <Tag
                            key={index}
                            size="md"
                            borderRadius="full"
                            variant="subtle"
                            colorScheme="green"
                            py={1.5}
                            px={3}
                          >
                            {benefit.trim()}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Job Info */}
              <div>
                <div className="bg-white rounded-lg p-4 sticky top-20 border border-gray-200 shadow-sm">
                  <div className="mb-6">
                    <div className="text-2xl font-bold text-gray-900">{formatSalary()}</div>
                    <div className="text-sm text-gray-500">Compensation</div>
                  </div>

                  <div className="space-y-5">
                    {/* Industry */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                        <FaBriefcase className="text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Industry</div>
                        <div className="font-medium text-gray-900">
                          {job.department?.title || "Information Technology"}
                        </div>
                      </div>
                    </div>

                    {/* Employment Type */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                        <FaCalendarAlt className="text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Employment Type</div>
                        <div className="font-medium text-gray-900">{job.job_type || "Full-time"}</div>
                      </div>
                    </div>

                    {/* Job Function */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                        <FaUserTie className="text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Job Function</div>
                        <div className="font-medium text-gray-900">
                          {job.job_title?.title || "Information technology"}
                        </div>
                      </div>
                    </div>

                    {/* Experience */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                        <FaUsers className="text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Experience Required</div>
                        <div className="font-medium text-gray-900">{experienceLabel}</div>
                      </div>
                    </div>

                    {/* Education (first chain or fallback) */}
                    {educationChains.length > 0 && (
                      <div className="flex items-center">
                        <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                          <FaGraduationCap className="text-blue-500" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Education</div>
                          <div className="font-medium text-gray-900">
                            {educationChains[0]?.title || "Not specified"}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Interview Type */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                        <FaVideo className="text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Interview Type</div>
                        <div className="font-medium text-gray-900">{job.interview_type || "Not specified"}</div>
                      </div>
                    </div>

                    {/* Deadline */}
                    {job.job_deadline && (
                      <div className="flex items-center">
                        <div className="w-8 h-8 mr-4 flex items-center justify-center bg-blue-50 rounded-full">
                          <FaCalendarAlt className="text-blue-500" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Application Deadline</div>
                          <div className="font-medium text-gray-900">
                            {new Date(job.job_deadline).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <Divider my={3} />

                  <div>
                    <div className="text-sm text-gray-500 mb-3">Share this job</div>
                    <div className="flex justify-center space-x-3">
                      <ShareButton
                        platform="facebook"
                        onClick={() =>
                          shareOnFacebook({
                            url: shareUrl,
                            quote: shareTitle,
                            hashtag: "#jobs",
                          })
                        }
                      >
                        <CustomIcon platform="facebook" size={20} />
                      </ShareButton>
                      <ShareButton
                        platform="twitter"
                        onClick={() =>
                          shareOnTwitter({
                            url: shareUrl,
                            title: shareTitle,
                            hashtags: ["jobs", "career", "hiring"],
                          })
                        }
                      >
                        <CustomIcon platform="twitter" size={20} />
                      </ShareButton>
                      <ShareButton
                        platform="linkedin"
                        onClick={() =>
                          shareOnLinkedIn({
                            url: shareUrl,
                            title: shareTitle,
                            summary: shareDescription,
                          })
                        }
                      >
                        <CustomIcon platform="linkedin" size={20} />
                      </ShareButton>
                    </div>
                    <button
                      onClick={copyToClipboard}
                      className="w-full mt-3 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Copy Job Link
                    </button>
                  </div>

                  {/* Apply Button (Mobile) */}
                  <div className="mt-6 md:hidden">
                    {job.is_expired === 1 ? (
                      <div className="text-red-500 font-medium text-center py-2 bg-red-50 rounded-lg border border-red-100">
                        Job expired, can't apply!
                      </div>
                    ) : job.already_applied ? (
                      <div className="text-green-600 font-medium text-center py-2 bg-green-50 rounded-lg border border-green-100">
                        ✓ You've already applied
                      </div>
                    ) : (
                      <button
                        className="w-full py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium shadow-sm"
                        onClick={handleApplyClick}
                      >
                        Apply Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Related Jobs Section */}
            <div className="mt-10">
              <RelatedJobs uuid={job.uuid} />
            </div>
          </div>

          {/* Apply Modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl"  >
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalContent maxW="800px" mx={{ base: 4, md: 0 }} >
                <ModalHeader
                  fontSize="xl"
                  fontWeight="bold"
                  color="gray.800"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  pb={4}
                >
                  Apply for {job.job_title?.title || job.job_name}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={6} >
                  <VStack spacing={6} align="stretch">
                    {/* Company and Job Info */}
                    <Flex alignItems="center" gap={3} bg="gray.50" p={3} borderRadius="md">
                      <Avatar
                        size="md"
                        src={job.company_logo ? `${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}` : null}
                        icon={<FaBuilding />}
                        bg="blue.100"
                      />
                      <Box>
                        <Text fontWeight="bold">{job.hiring_company_name}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {job.job_title?.title || job.job_name}
                        </Text>
                      </Box>
                    </Flex>

                    {/* Resume Option Dropdown */}
                    <Box>
                      <FormControl isInvalid={!!errors.hasResume}>
                        <Text mb={2} fontWeight="medium">
                          Resume Options
                        </Text>
                        <Select
                          {...register("hasResume", {
                            required: "This field is required",
                          })}
                          placeholder="Select Resume Option"
                          variant="filled"
                          borderColor={errors.hasResume ? "red.500" : "gray.200"}
                          onChange={(e) => setHasResume(e.target.value === "true")}
                        >
                          <option value="false">Apply without Resume</option>
                          <option value="true">Upload Resume</option>
                        </Select>
                        <FormErrorMessage>{errors.hasResume?.message}</FormErrorMessage>
                      </FormControl>
                    </Box>

                    {/* Resume Upload Section */}
                    {hasResume && (
                      <Box
                        borderWidth="2px"
                        borderRadius="lg"
                        borderStyle="dashed"
                        borderColor="blue.300"
                        p={4}
                        bg="blue.50"
                        transition="all 0.3s"
                        _hover={{
                          bg: "blue.100",
                          borderColor: "blue.400",
                        }}
                      >
                        <input
                          type="file"
                          id="fileUpload"
                          accept=".pdf,.docx,.rtf,.txt"
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                        />
                        {!resumePreview ? (
                          <Flex
                            alignItems="center"
                            cursor="pointer"
                            onClick={() => document.getElementById("fileUpload").click()}
                          >
                            <FaFileInvoice size={24} className="text-blue-500 mr-3" />
                            <Box>
                              <Box fontWeight="bold" color="blue.700">
                                Upload CV
                              </Box>
                              <Box color="gray.600" fontSize="sm">
                                PDF, DOCX, RTF, or TXT (max 5MB)
                              </Box>
                            </Box>
                          </Flex>
                        ) : (
                          <Flex alignItems="center" justifyContent="space-between">
                            <Flex alignItems="center">
                              <FaFileInvoice size={24} color="green" style={{ marginRight: "12px" }} />
                              <Box>
                                <Box fontWeight="bold" color="green.700">
                                  {resumePreview.name}
                                </Box>
                                <Box color="gray.500" fontSize="xs">
                                  {(resumePreview.size / 1024).toFixed(2)} KB
                                </Box>
                              </Box>
                            </Flex>
                            <Button size="sm" colorScheme="red" variant="outline" onClick={removeResume}>
                              Remove
                            </Button>
                          </Flex>
                        )}
                      </Box>
                    )}

                    {/* Application Note Textarea */}
                    <Box>
                      <Text mb={2} fontWeight="medium">
                        Cover Letter / Note (Optional)
                      </Text>
                      <Textarea
                        placeholder="Add a note for your application..."
                        {...register("apply_note")}
                        variant="filled"
                        h="200px"
                        borderColor="gray.200"
                      />
                    </Box>
                  </VStack>
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.200" pt={4}>
                  <Button variant="outline" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" type="submit">
                    Submit Application
                  </Button>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>

          <Footer />
        </div>
      ) : (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mb-2.5"></div>
            <div className="h-3 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      )}
    </>
  )
}

export default JobSingle2